/**
 * AI Observability types — the Overview, calls and their layers, and the
 * Observability Master.
 *
 * Every cost and currency is nullable ON PURPOSE: `null` means UNKNOWN (no
 * pricing, or a provider that reported no tokens), and rendering it as `0`
 * would turn "we don't know" into "it was free". `fmtCost` is the only place
 * that decision is made.
 */

export interface AiObservabilityParams {
  timeRange?: string;
  fromDate?: string;
  toDate?: string;
}

export const OBS_TIME_RANGES = [
  { label: 'Last hour', value: '1h' },
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
] as const;

/**
 * Money, or an explicit unknown.
 *
 * `null` renders as an em dash, never as 0.00 — the backend uses null to mean
 * "this was not measured", and collapsing that to zero is how a real cost
 * becomes invisible. A null CURRENCY with a real amount means the figure spans
 * several currencies, so the amount is shown bare rather than under a symbol
 * that would be wrong for part of it.
 */
export const fmtCost = (cost: number | null | undefined, currency?: string | null): string => {
  if (cost === null || cost === undefined) return '—';
  const digits = cost !== 0 && Math.abs(cost) < 0.01 ? 6 : 2;
  const amount = cost.toFixed(digits);
  if (!currency) return amount;
  return `${currency === 'USD' ? '$' : currency === 'INR' ? '₹' : currency + ' '}${amount}`;
};

/** Milliseconds, in whatever unit reads best at that magnitude. */
export const fmtMs = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)} s`;
};

/** Large token counts, compacted. */
export const fmtTokens = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};

/**
 * Why reasoning is or is not present. The distinction matters: an empty panel
 * cannot tell you whether the model thought and we lost it, thought and the
 * provider withheld the text, chose not to think, or cannot think at all.
 *
 * `null` is a fifth case — a run recorded before the field existed. It is left
 * as unknown rather than defaulted, because defaulting it to "unsupported"
 * would assert a capability claim nobody made.
 */
export type ObsReasoningState = 'captured' | 'tokens_only' | 'none' | 'unsupported';

/**
 * What to show where the reasoning would be, when there is none to show.
 * Returns null when there IS reasoning — the text speaks for itself.
 */
export const reasoningNote = (
  state: ObsReasoningState | null | undefined,
  tokens: number | null | undefined,
): string | null => {
  switch (state) {
    case 'captured':
      return null;
    case 'tokens_only':
      return tokens
        ? `The model reasoned for ${tokens.toLocaleString()} tokens, but this provider bills for the thinking without returning it.`
        : 'The model reasoned, but this provider does not return the text.';
    case 'none':
      return 'This model can reason and did not on this call.';
    case 'unsupported':
      return 'This model does not produce reasoning.';
    default:
      return 'Not recorded — this run predates reasoning capture.';
  }
};

/* ── Tracing: where each AI call went, and where it stopped ─────────────────── */

export type ObsKind =
  | 'PROMPT' | 'RAG' | 'EVAL' | 'AGENT' | 'INGESTION' | 'EMBEDDING'
  | 'JUDGE' | 'JOB' | 'MODEL_TEST' | 'DOCUMENT';

export type ObsStepStatus = 'OK' | 'FAILED' | 'WARN';

export interface ObsStepError {
  type: string;
  message: string;
  status: number | null;
  code: string | null;
}

/**
 * One stage of a call. WARN entries are degraded paths that still answered —
 * a search that fell back, a placeholder nobody supplied — and are usually the
 * explanation for a bad answer that raised no error.
 */
export interface ObsStep {
  name: string;
  startMs: number;
  durationMs: number | null;
  status: ObsStepStatus;
  attrs?: Record<string, unknown>;
  error?: ObsStepError | null;
}

export interface ObsTraceRow {
  observabilityId: string;
  traceId: string | null;
  kind: ObsKind;
  mode: string | null;
  name: string | null;
  status: string | null;
  failedStep: string | null;
  errorMessage: string | null;
  warning: string | null;
  warningCount: number;
  modelId: string | null;
  latencyMs: number | null;
  cost: number | null;
  currency: string | null;
  question: string | null;
  /** The operation this ran inside — an eval run, for its cases and judges. */
  parentObservabilityId: string | null;
  /** What an agent actually called, and how often. */
  tools: { name: string; calls: number }[] | null;
  steps: ObsStep[];
  /** false = written before step capture; it has no waterfall to draw. */
  traced: boolean;
  startedAt: string;
}

export interface ObsTraceList {
  summary: {
    calls: number;
    failed: number;
    withWarnings: number;
    untraced: number;
    from: string;
    to: string;
  };
  failuresByStep: { step: string; count: number }[];
  traces: ObsTraceRow[];
}

/** The knowledge base behind a retrieval, as it was when the index was built. */
export interface ObsKbLineage {
  pipeline?: { code: string | null; runRef: string | null;
    sourcesFailed: number | null; chunksCreated: number | null };
  parser?: Record<string, unknown> | null;
  cleaning?: Record<string, unknown> | null;
  chunking?: { strategy?: string; chunkSize?: number; chunkOverlap?: number } & Record<string, unknown> | null;
  embedding?: { model: string | null; dimensions: number | null };
  store?: { provider: string | null; index: string | null; artifactCode: string | null;
    artifactVersion: number | null; chunkCount: number | null; sourceCount: number | null };
  retrieval?: { type: string; topK: number | null; minimumScore: number | null;
    maxContextChunks: number | null; maxContextTokens: number | null;
    maxPerSource: number | null; reranking: boolean };
  generation?: { enabled: boolean; promptId: string | null; modelRef: string | null;
    maxTokens: number | null };
  version?: { ragCode: string | null; version: number | null; status: string | null };
}

export interface ObsRetrievedPassage {
  content: string | null;
  source: string | null;
  page: number | null;
  rank: number | null;
  score: number | null;
}

/** A related operation — parent or child — named enough to link to. */
export interface ObsTraceBrief {
  observabilityId: string;
  kind?: ObsKind;
  name?: string | null;
  status?: string | null;
  failedStep?: string | null;
  modelId?: string | null;
  latencyMs?: number | null;
  cost?: number | null;
  currency?: string | null;
  startedAt?: string;
  warningCount?: number;
  /** The parent is still running, so its row does not exist yet. */
  pending?: boolean;
}

/* ── Trace layers: input → context → reasoning → answer → checks ─────────── */

export type ObsLayerName = 'input' | 'context' | 'reasoning' | 'answer' | 'checks';
export type ObsLayerStatus = 'ok' | 'warn' | 'fail' | 'na' | 'hidden';

export interface ObsLayerFlag {
  level: 'warn' | 'fail';
  name: string;
  message: string;
}

export interface ObsGroundingVerdict { score: number | null; reason: string | null; error: string | null }

export interface ObsGrounding {
  judgeModel: string | null;
  /** 'retrieved passages' for RAG, 'prompt' for a prompt run. */
  against: string;
  estimate: true;
  checkedAt: string;
  cost: number | null;
  /** False when the check ran but could not be saved on the trace. */
  stored?: boolean;
  reasoning?: ObsGroundingVerdict;
  answer?: ObsGroundingVerdict;
}

export interface ObsLayer {
  status: ObsLayerStatus;
  flags: ObsLayerFlag[];
  /** Why a layer has nothing to show (retrieval only, not captured…). */
  note?: string;
  // input
  question?: string | null; prompt?: string | null; system?: string | null;
  variables?: Record<string, unknown> | null; ragCode?: string | null; version?: number | null;
  // context
  passages?: ObsRetrievedPassage[]; chunkCount?: number | null; sourceCount?: number | null;
  // reasoning / answer
  text?: string | null; state?: string | null; tokens?: number | null;
  model?: string | null; stopReason?: string | null;
  // checks
  citations?: { index?: number; source?: string; page?: number }[];
  grounding?: ObsGrounding;
}

export interface ObsLayers {
  order: ObsLayerName[];
  layers: Record<ObsLayerName, ObsLayer>;
  /** The first layer that went wrong, or null when none did. */
  firstProblem: ObsLayerName | null;
  canCheckGrounding: boolean;
}

export interface ObsTraceDetail extends ObsTraceRow {
  /** The five layers — present for prompt runs and RAG queries. */
  layers?: ObsLayers | null;
  parent: ObsTraceBrief | null;
  children: ObsTraceBrief[];
  steps: ObsStep[];
  warnings: ObsStep[];
  errorCode: string | null;
  providerType: string | null;
  servedModel: string | null;
  usage: Record<string, number | null> | null;
  reasoningState: ObsReasoningState | null;
  tools: { name: string; calls: number }[] | null;
  retrieval: Record<string, unknown> | null;
  kb: ObsKbLineage | null;
  sourceCollection: string | null;
  /** The Observability Master's content capture is off for this type. */
  contentHidden?: boolean;
  /** What the Observability Master told this row not to keep. */
  notCaptured?: ObsCapture[];
}

/** Plain names for the stages, in the words someone debugging would use. */
export const STEP_LABEL: Record<string, string> = {
  load_prompt: 'Load prompt',
  render: 'Render prompt',
  resolve_model: 'Resolve model',
  call_model: 'Call the model',
  resolve_rag: 'Resolve RAG version',
  load_kb: 'Load knowledge base',
  embed_question: 'Embed the question',
  search: 'Search the index',
  select_context: 'Select context',
  generate: 'Generate answer',
  cite: 'Check citations',
  resolve_agent: 'Resolve agent',
  select_tools: 'Select tools',
  compose_task: 'Compose task',
  compose_rubric: 'Compose rubric',
  call_judge: 'Call the judge',
  parse_verdict: 'Read the verdict',
  get_client: 'Get LLM client',
  build_client: 'Build client',
  embed: 'Embed',
  load_pipeline: 'Load pipeline',
  resolve_embedding_model: 'Resolve embedding model',
  probe_dimensions: 'Probe dimensions',
  ensure_index: 'Ensure index',
  create_artifact: 'Create artifact',
  check_reuse: 'Check what can be reused',
  process_sources: 'Parse, chunk and embed sources',
  finalize: 'Finalise',
  agent_lookup_fallback: 'Fell back to code-registered agent',
  no_tool_called: 'Agent called no tool',
  unknown_tool: 'Called a tool it was never offered',
  repeated_tool_call: 'Same tool called repeatedly',
  full_rebuild: 'Incremental run rebuilt everything',
  source_failed: 'Source failed',
  candidate_version: 'Candidate version answered',
  lexical_fallback: 'Hybrid search degraded',
  no_context: 'No context retrieved',
  context_appended: 'Context appended, not placed',
  unresolved_placeholders: 'Placeholder never supplied',
  truncated: 'Answer truncated',
  invalid_citations: 'Cites passages that do not exist',
  uncited: 'Answer cites nothing',
  ungrounded: 'Not supported by what it was given',
  failed: 'Call failed',
  unrecorded: 'Not recorded',
};

export const stepLabel = (name: string | null | undefined): string =>
  (name && STEP_LABEL[name]) || name || '—';

export const STEP_COLOR: Record<ObsStepStatus, string> = {
  OK: '#6366F1',
  FAILED: '#EF4444',
  WARN: '#F59E0B',
};

/** Colour per subsystem, shared by chips across the section. */
export const KIND_COLOR: Record<string, string> = {
  PROMPT: '#6366F1',
  RAG: '#06B6D4',
  EVAL: '#22C55E',
  AGENT: '#A855F7',
  INGESTION: '#F59E0B',
  EMBEDDING: '#64748B',
  JUDGE: '#EC4899',
  JOB: '#0EA5E9',
  MODEL_TEST: '#94A3B8',
  DOCUMENT: '#14B8A6',
};

/** How each kind reads in a filter or a chip. */
export const KIND_LABEL: Record<string, string> = {
  PROMPT: 'Prompt',
  RAG: 'RAG',
  AGENT: 'Agent',
  EVAL: 'Eval run',
  JUDGE: 'Judge',
  INGESTION: 'Ingestion',
  EMBEDDING: 'Embedding',
  JOB: 'Job',
  MODEL_TEST: 'Model test',
  DOCUMENT: 'Document analysis',
};

export const kindLabel = (kind: string | null | undefined): string =>
  (kind && KIND_LABEL[kind]) || kind || '—';

export const MODE_LABEL: Record<string, string> = {
  ONE_SHOT: 'one-shot',
  REASONING: 'reasoning',
  RAG_CONTEXTUAL: 'RAG context',
  RETRIEVAL_ONLY: 'retrieval only',
  AGENT_TOOLS: 'tools',
  NOT_APPLICABLE: 'n/a',
  UNKNOWN: 'unrecorded',
};

/* ── Observability Master: which AI work is observed, and what it keeps ─────── */

export type ObsType = 'REASONING' | 'ONE_SHOT' | 'RAG_CONTEXTUAL' | 'AGENT_TOOLS' | 'EVAL' | 'SYSTEM';
export type ObsCapture = 'content' | 'reasoning' | 'kb' | 'tools' | 'steps' | 'usage';

export interface ObsTypeRule {
  /** Off still records failures. */
  observe: boolean;
  capture: Record<ObsCapture, boolean>;
}

export interface ObsSettings {
  enabled: boolean;
  types: Record<ObsType, ObsTypeRule>;
}

export interface ObsSettingsResponse {
  settings: ObsSettings;
  defaults: ObsSettings;
  types: ObsType[];
  /** Which capture switches mean anything for each type. */
  applies: Record<ObsType, ObsCapture[]>;
  usage: {
    byType: Record<ObsType, { total: number; lastWeek: number; failed: number }>;
    totalRows: number;
  };
}

export const OBS_TYPE_INFO: Record<ObsType, { label: string; what: string }> = {
  REASONING: { label: 'Reasoning', what: 'Model calls that think before answering (Gemini, Qwen…).' },
  ONE_SHOT: { label: 'One-shot', what: 'Plain prompt calls — no thinking, no retrieval, no tools.' },
  RAG_CONTEXTUAL: { label: 'RAG contextual', what: 'Knowledge-base questions: search, chunks, answer, citations.' },
  AGENT_TOOLS: { label: 'Agents & tools', what: 'Agent calls: which tools were offered and which were called.' },
  EVAL: { label: 'Evals', what: 'Eval runs and the judge calls that grade them.' },
  SYSTEM: { label: 'System', what: 'Ingestion, embeddings, model tests and document OCR.' },
};

export const OBS_CAPTURE_INFO: Record<ObsCapture, { label: string; what: string }> = {
  content: { label: 'Prompt & answer text', what: 'Question, answer and retrieved passages. Off = names and numbers only.' },
  reasoning: { label: 'Reasoning', what: 'Whether the model thought, and how many thinking tokens.' },
  kb: { label: 'KB detail', what: 'Chunking method, parser, embedding model and store used.' },
  tools: { label: 'Tool calls', what: 'Which tools the model called and how often.' },
  steps: { label: 'Stage steps', what: 'The full where-it-went waterfall. Off keeps only failed and warning steps.' },
  usage: { label: 'Cost & tokens', what: 'Token counts and cost.' },
};

/* ── Overview: how the AI did ─────────────────────────────────────────────── */

/** Where a problem sits: setup (before the AI runs), then the five layers. */
export type ObsStage = 'setup' | ObsLayerName;

export interface ObsAttention {
  name: string;
  kind: ObsKind;
  problem: string;
  level: 'fail' | 'warn';
  layer: ObsStage;
  message: string;
  count: number;
  lastAt: string;
  latestId: string;
}

export interface ObsOverview {
  window: { from: string; to: string };
  /** The Master's switch: false means nothing new is being recorded. */
  enabled: boolean;
  summary: { calls: number; ok: number; failed: number; wentOff: number;
    /** Over fully observed types only. */
    healthyRate: number | null;
    /** Calls of types switched to failures-only, left out of healthyRate. */
    failuresOnlyCalls: number;
    truncated: boolean };
  byLayer: { layer: ObsStage; fail: number; warn: number }[];
  attention: ObsAttention[];
  attentionTotal: number;
  byType: { type: ObsType; calls: number; failed: number; wentOff: number; cost: number | null;
    /** False when the Master records only this type's failures. */
    observing: boolean }[];
  recent: ObsTraceRow[];
  spend: { cost: number | null; currency: string | null;
    /** Calls whose cost the Master's "Cost & tokens" switch dropped. */
    costNotCaptured: number;
    /** Judge calls behind grounding checks — in the cost, not in the counts. */
    groundingChecks: number;
    avgLatencyMs: number | null; p95LatencyMs: number | null };
}
